import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate } from 'motion/react';
import {
  BLOOM_SPRING,
  GROW_TRANSITION,
  RING_CIRCUMFERENCE,
  RING_RADIUS,
  RING_STROKE,
  SOIL_Y,
  growthFlowerTestId,
  lerpPose,
  poseForGrowthLevel,
  ringDashOffset,
  shouldPopBloom,
  type FlowerPose,
} from './flowerPose';

const STEM = '#293749';
const LEAF = '#04CB93';
const PETAL = '#FFBB00';
const BLOOM_LIFT = 1.55;

const LEFT_LEAF =
  'M0 0C-0.15-1.35-2.15-2.45-3.35-1.25C-4.15 0.15-2.35 1.85-0.25 0.55C-0.15 0.25 0 0.05 0 0Z';
const RIGHT_LEAF =
  'M0 0C0.15-1.45 2.35-2.65 3.55-1.35C4.35 0.2 2.45 1.95 0.25 0.6C0.15 0.28 0 0.06 0 0Z';
const MINT_BUD = 'M0 0.85C-1.15 0.35-1.25-1.55 0-2.25C1.25-1.55 1.15 0.35 0 0.85Z';
const YELLOW_LEFT = 'M0.1 0.9C-1.7 0.55-2.35-1.35-0.15-2.55C0.35-0.85 0.4 0.15 0.1 0.9Z';
const YELLOW_RIGHT = 'M-0.1 0.9C1.7 0.55 2.35-1.35 0.15-2.55C-0.35-0.85-0.4 0.15-0.1 0.9Z';
const YELLOW_CENTER = 'M0 0.7C-0.95 0.25-0.75-1.7 0-2.15C0.75-1.7 0.95 0.25 0 0.7Z';
const SEPAL =
  'M0 1.05C-1.55 0.85-2.05-0.15-1.15-0.85C-0.55 0.05 0.55 0.05 1.15-0.85C2.05-0.15 1.55 0.85 0 1.05Z';

const PETAL_TURNS = [0, 60, 120, 180, 240, 300] as const;

const SOIL_PATH =
  'M3.24 17.6H20.76C19.8194 19.0699 18.524 20.2795 16.9931 21.1174C15.4622 21.9553 13.7451 22.3944 12 22.3944C10.2548 22.3944 8.53775 21.9553 7.00689 21.1174C5.47603 20.2795 4.18062 19.0699 3.24 17.6Z';

const leafAnchorY = (stemTop: number, along: number) => SOIL_Y - (SOIL_Y - stemTop) * along;

const fadeIn = (value: number) => Math.min(1, Math.max(0, value) * 3);

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type AnimatedFlowerProps = {
  growthLevel: number;
  /** When this changes, the flower shows the new word's level without growing. */
  learnableId?: number;
};

export const AnimatedFlower: FC<AnimatedFlowerProps> = ({ growthLevel, learnableId }) => {
  const [pose, setPose] = useState<FlowerPose>(() => poseForGrowthLevel(growthLevel));
  const poseRef = useRef(pose);
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const skipAnimation = useRef(true);
  const levelRef = useRef(growthLevel);
  const learnableRef = useRef(learnableId);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    const target = poseForGrowthLevel(growthLevel);
    const from = poseRef.current;
    const sameLearnable = learnableRef.current === learnableId;
    const grew = growthLevel > levelRef.current;
    const pop = sameLearnable && shouldPopBloom(levelRef.current, growthLevel);
    const instant =
      skipAnimation.current || reduced || prefersReducedMotion() || !sameLearnable || !grew;

    skipAnimation.current = false;
    learnableRef.current = learnableId;
    levelRef.current = growthLevel;

    const show = (next: FlowerPose) => {
      poseRef.current = next;
      setPose(next);
    };

    if (instant) {
      show(target);
      return undefined;
    }

    let progress = 0;
    let bloomValue = from.bloom;
    const publish = () => show(lerpPose(from, target, progress, pop ? bloomValue : undefined));

    const controls = [
      animate(0, 1, {
        ...GROW_TRANSITION,
        onUpdate: value => {
          progress = value;
          publish();
        },
      }),
    ];
    if (pop) {
      controls.push(
        animate(from.bloom, target.bloom, {
          ...BLOOM_SPRING,
          onUpdate: value => {
            bloomValue = value;
            publish();
          },
        }),
      );
    }

    return () => controls.forEach(control => control.stop());
  }, [growthLevel, learnableId, reduced]);

  const leftLeaf = `translate(12 ${leafAnchorY(pose.stemTop, 0.42)}) rotate(-18) scale(${pose.leaf})`;
  const rightLeaf = `translate(12 ${leafAnchorY(pose.stemTop, 0.62)}) rotate(16) scale(${pose.leaf})`;

  return (
    <div data-testid={growthFlowerTestId(growthLevel)} data-growth-level={growthLevel} aria-hidden>
      <svg viewBox="0 0 24 24" fill="none" overflow="visible">
        <circle
          cx="12"
          cy="12"
          r={RING_RADIUS}
          stroke="#E7EAF0"
          strokeWidth={RING_STROKE}
          fill="none"
        />
        <g transform="rotate(-90 12 12)">
          <circle
            data-testid="flower-progress"
            cx="12"
            cy="12"
            r={RING_RADIUS}
            stroke={PETAL}
            strokeWidth={RING_STROKE}
            fill="none"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={ringDashOffset(pose.ring)}
          />
        </g>
        <path d={SOIL_PATH} fill="#00A778" />
        <line
          x1="12"
          y1={SOIL_Y}
          x2="12"
          y2={pose.stemTop}
          stroke={STEM}
          strokeWidth="0.6"
          strokeLinecap="round"
        />
        <g transform={leftLeaf} opacity={fadeIn(pose.leaf)}>
          <path d={LEFT_LEAF} fill={LEAF} stroke={STEM} strokeWidth="0.45" strokeLinejoin="round" />
          <path d="M-0.2 0.2L-2.15-0.45" stroke={STEM} strokeWidth="0.35" strokeLinecap="round" />
        </g>
        <g transform={rightLeaf} opacity={fadeIn(pose.leaf)}>
          <path
            d={RIGHT_LEAF}
            fill={LEAF}
            stroke={STEM}
            strokeWidth="0.45"
            strokeLinejoin="round"
          />
          <path d="M0.2 0.22L2.25-0.5" stroke={STEM} strokeWidth="0.35" strokeLinecap="round" />
        </g>
        <g
          transform={`translate(12 ${pose.stemTop}) scale(${pose.mintBud})`}
          opacity={fadeIn(pose.mintBud)}
        >
          <path
            d={MINT_BUD}
            fill="#5DE7C0"
            stroke={STEM}
            strokeWidth="0.4"
            strokeLinejoin="round"
          />
        </g>
        <g
          data-testid="flower-bud"
          transform={`translate(12 ${pose.stemTop}) scale(${pose.yellowBud})`}
          opacity={fadeIn(pose.yellowBud)}
        >
          <path d={SEPAL} fill={LEAF} stroke={STEM} strokeWidth="0.4" strokeLinejoin="round" />
          <path
            d={YELLOW_LEFT}
            fill="#E9AC05"
            stroke={STEM}
            strokeWidth="0.4"
            strokeLinejoin="round"
          />
          <path
            d={YELLOW_RIGHT}
            fill={PETAL}
            stroke={STEM}
            strokeWidth="0.4"
            strokeLinejoin="round"
          />
          <path
            d={YELLOW_CENTER}
            fill={PETAL}
            stroke={STEM}
            strokeWidth="0.4"
            strokeLinejoin="round"
          />
        </g>
        <g
          data-testid="flower-bloom"
          transform={`translate(12 ${pose.stemTop - BLOOM_LIFT}) scale(${Math.max(0, pose.bloom)})`}
          opacity={Math.min(1, Math.max(0, pose.bloom) * 4)}
        >
          {PETAL_TURNS.map(turn => (
            <ellipse
              key={turn}
              cx="0"
              cy="-2.55"
              rx="1.4"
              ry="1.85"
              transform={`rotate(${turn})`}
              fill={PETAL}
              stroke={STEM}
              strokeWidth="0.4"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <circle r="1.05" fill="#ED6B35" stroke={STEM} strokeWidth="0.4" />
          <circle cx="-0.38" cy="-0.4" r="0.32" fill="#FFF1CC" />
        </g>
      </svg>
    </div>
  );
};
